/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createHook,
	createStore,
	type BoundActions,
	type HookFunction,
	type OverrideContainerComponent,
	type Store,
	type StoreActionApi,
} from '@atlassian/react-sweet-state';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { actions, type Actions } from './knowledgePerformanceStoreActions.tsx';
import { getSuggestedTopics, getPageNumber } from './getPageNumber.tsx';
import type { State, Props, SuggestedTopicState } from './SuggestedTopicState.types.tsx';

const initialState: State = {
	topics: {
		data: [],
		loading: false,
		error: null,
	},
	page: 1,
};
export type ImmutableState = Readonly<State>;

export const store: Store<
	State,
	{
		readonly fetchSuggestedTopics: (
			projectId: number,
			analyticsEvent: UIAnalyticsEvent,
			activationId: string,
		) => ({ getState, setState }: StoreActionApi<State>) => Promise<void>;
		readonly setPagination: (
			currentPage: number,
		) => ({ getState, setState }: StoreActionApi<State>) => Promise<void>;
		readonly dismissTopic: (
			topicId: string,
		) => ({ getState, setState }: StoreActionApi<State>, props: Props) => Promise<void>;
		readonly fetchAiDraftPreviewContent: (
			topicId: string,
		) => ({ getState, setState }: StoreActionApi<State>, props: Props) => Promise<void>;
		readonly triggerArticleGenerationForTopics: (
			projectId: number,
			analyticsEvent: UIAnalyticsEvent,
			activationId: string,
		) => () => Promise<void>;
	},
	unknown
> = createStore<State, Actions>({
	initialState,
	actions,
	name: 'KnowledgeHubSuggestedTopicsStore',
});

export const KnowledgeHubSuggestedTopicsContainer: OverrideContainerComponent<Props> =
	createContainer<State, Actions, Props>(store, {
		displayName: 'KnowledgeHubSuggestedTopicsContainer',
	});

export const useSuggestedTopics: HookFunction<
	SuggestedTopicState,
	BoundActions<
		State,
		{
			readonly fetchSuggestedTopics: (
				projectId: number,
				analyticsEvent: UIAnalyticsEvent,
				activationId: string,
			) => ({ getState, setState }: StoreActionApi<State>) => Promise<void>;
			readonly setPagination: (
				currentPage: number,
			) => ({ getState, setState }: StoreActionApi<State>) => Promise<void>;
			readonly dismissTopic: (
				topicId: string,
			) => ({ getState, setState }: StoreActionApi<State>, props: Props) => Promise<void>;
			readonly fetchAiDraftPreviewContent: (
				topicId: string,
			) => ({ getState, setState }: StoreActionApi<State>, props: Props) => Promise<void>;
			readonly triggerArticleGenerationForTopics: (
				projectId: number,
				analyticsEvent: UIAnalyticsEvent,
				activationId: string,
			) => () => Promise<void>;
		}
	>,
	void
> = createHook(store, {
	selector: getSuggestedTopics,
});

export const usePageNumber: HookFunction<
	number,
	BoundActions<
		State,
		{
			readonly fetchSuggestedTopics: (
				projectId: number,
				analyticsEvent: UIAnalyticsEvent,
				activationId: string,
			) => ({ getState, setState }: StoreActionApi<State>) => Promise<void>;
			readonly setPagination: (
				currentPage: number,
			) => ({ getState, setState }: StoreActionApi<State>) => Promise<void>;
			readonly dismissTopic: (
				topicId: string,
			) => ({ getState, setState }: StoreActionApi<State>, props: Props) => Promise<void>;
			readonly fetchAiDraftPreviewContent: (
				topicId: string,
			) => ({ getState, setState }: StoreActionApi<State>, props: Props) => Promise<void>;
			readonly triggerArticleGenerationForTopics: (
				projectId: number,
				analyticsEvent: UIAnalyticsEvent,
				activationId: string,
			) => () => Promise<void>;
		}
	>,
	void
> = createHook(store, {
	selector: getPageNumber,
});
